import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';

const RESEND_COOLDOWN_SECONDS = 60;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  identifierForm!: UntypedFormGroup;
  codeForm!: UntypedFormGroup;
  siteKey: string = environment.siteKey;
  showCaptcha: boolean = true;
  loading = false;
  codeSent = false;
  resendCountdown = 0;

  private identifier: string;
  private returnUrl!: string;
  private countdown: any;

  constructor(private route: ActivatedRoute, private router: Router,
    private userService: UserService, private notificationService: NotificationService) { }

  ngOnInit() {
    this.identifierForm = new UntypedFormGroup({
      identifier: new UntypedFormControl('', [Validators.required]),
      reCaptcha: new UntypedFormControl(null, [Validators.required])
    });

    this.codeForm = new UntypedFormGroup({
      code: new UntypedFormControl('', [Validators.required]),
      reCaptcha: new UntypedFormControl(null, [Validators.required])
    });

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  ngOnDestroy() {
    this.stopResendCountdown();
  }

  sendCode() {
    if (this.identifierForm.invalid) {
      return;
    }

    this.loading = true;
    const identifier = this.sanitize(this.identifierForm.value.identifier);

    this.userService.sendOTP(identifier, this.identifierForm.value.reCaptcha).subscribe({
      next: status => {
        this.loading = false;

        if (!status.wasSuccessful) {
          this.notificationService.error(status.message);
          this.resetCaptcha(this.identifierForm);
          return;
        }

        this.identifierForm.patchValue({ reCaptcha: null });
        this.identifier = identifier;
        this.codeSent = true;
        this.startResendCountdown();
        this.notificationService.info('Enviamos um código de acesso. Ele é válido por 5 minutos.');
      },
      error: error => {
        this.loading = false;
        this.notificationService.error(error);
        this.resetCaptcha(this.identifierForm);
      }
    });
  }

  resendCode() {
    if (this.resendCountdown > 0 || this.codeForm.controls.reCaptcha.invalid) {
      return;
    }

    this.loading = true;

    this.userService.sendOTP(this.identifier, this.codeForm.value.reCaptcha).subscribe({
      next: status => {
        this.loading = false;
        this.resetCaptcha(this.codeForm);

        if (!status.wasSuccessful) {
          this.notificationService.error(status.message);
          return;
        }

        this.startResendCountdown();
        this.notificationService.info('Um novo código de acesso foi enviado.');
      },
      error: error => {
        this.loading = false;
        this.notificationService.error(error);
        this.resetCaptcha(this.codeForm);
      }
    });
  }

  logIn() {
    if (this.codeForm.invalid) {
      return;
    }

    this.loading = true;

    this.userService.validateOTP(this.identifier, this.codeForm.value.code, this.codeForm.value.reCaptcha).subscribe({
      next: () => {
        this.router.navigateByUrl(this.returnUrl);
      },
      error: error => {
        this.loading = false;
        this.notificationService.error(error);
        this.resetCaptcha(this.codeForm);
      }
    });
  }

  changeIdentifier() {
    this.stopResendCountdown();
    this.codeSent = false;
    this.identifier = null;
    this.codeForm.reset();
    this.resetCaptcha(this.identifierForm);
  }

  // the associate is identified by the CPF as it is stored, without formatting
  private sanitize(identifier: string) {
    identifier = identifier.trim();
    return identifier.includes('@') ? identifier : identifier.replace(/\D/g, '');
  }

  // the reCaptcha token is single use, so the widget must be recreated after every request
  private resetCaptcha(form: UntypedFormGroup) {
    form.patchValue({ reCaptcha: null });
    this.showCaptcha = false;

    setTimeout(() => {
      this.showCaptcha = true;
    });
  }

  private startResendCountdown() {
    this.stopResendCountdown();
    this.resendCountdown = RESEND_COOLDOWN_SECONDS;

    this.countdown = setInterval(() => {
      this.resendCountdown--;

      if (this.resendCountdown <= 0) {
        this.stopResendCountdown();
      }
    }, 1000);
  }

  private stopResendCountdown() {
    clearInterval(this.countdown);
    this.resendCountdown = 0;
  }
}
